import { motion } from "framer-motion";
import { MessageCircle, Globe } from "lucide-react";
import { business, waLink } from "../config/business";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { LANG_OPTIONS } from "../i18n/translations";
import { useScrolled } from "../hooks/useScrolled";

const LanguageSelect = ({ lang, setLang }) => (
  <div data-testid="header-lang-switcher" className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-colors duration-300 hover:border-white/20 hover:bg-white/[0.08]">
    <Globe className="h-3.5 w-3.5 text-yellow-400" />
    <select
      data-testid="header-lang-select"
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      aria-label="Language"
      className="cursor-pointer appearance-none bg-transparent pr-1 font-mono text-[11px] font-semibold tracking-wider text-slate-200 outline-none"
    >
      {LANG_OPTIONS.map((opt) => (
        <option key={opt.code} value={opt.code} data-testid={`lang-option-${opt.code}`} className="bg-[#11203A] text-slate-100">
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const Header = ({ hideLanguage = false }) => {
  const { lang, setLang, t } = useLanguage();
  const scrolled = useScrolled();

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl transition-all duration-500 ${
        scrolled
          ? "bg-[var(--bg-glass)] shadow-[0_1px_0_rgba(255,255,255,0.08),0_16px_40px_-20px_rgba(0,0,0,0.8)]"
          : "bg-[#0A1526]/50 shadow-[0_1px_0_rgba(255,255,255,0.04)]"
      }`}
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 sm:h-[4.5rem] flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo" className="group flex items-center gap-2.5 shrink-0">
          <img src="/images/logo.webp" alt={business.name} className="h-9 w-9 rounded-xl object-cover shadow-[0_6px_16px_-6px_rgba(234,179,8,0.5)] transition-transform duration-300 group-hover:scale-105" />
          <span className="font-heading font-bold text-base sm:text-lg tracking-[-0.02em]">
            {business.name}
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          {!hideLanguage && <LanguageSelect lang={lang} setLang={setLang} />}

          <a
            data-testid="header-whatsapp-button"
            href={waLink(t.hero.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-wa rounded-full px-4 sm:px-5 py-2.5 text-sm"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
            <span className="hidden xs:inline sm:inline">{t.header.whatsapp}</span>
          </a>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
